/* eslint-disable indent */
import {
  useState,
  useMemo,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';

import Modal from '@/components/Modal';
import { Select } from '@/components/Controls/Select';
import { damageLevelOptions, CORRECTION_TYPE } from '@/utils/constant.utils';
import {
  getColorByValue,
  convertRectangePointToYolo,
  getDamageValue,
} from '@/utils/ia.utils';
import { ElementType } from '@/entities/ia';
import { Rectangle, Polygon } from '@/entities/canvas';
import {
  findElementParentDmg,
  findDmgInsideEl,
  initReport,
} from '@/utils/ia.utils';
import { MODE_DRAW } from '@/utils/constant.utils';
import { useGetModerationEquipments } from '@/hooks/equipment.hooks';
import { useGetModerationDamages } from '@/hooks/damage.hooks';
import { TFunction } from 'i18next';
import { focusButton } from '@/utils/button.utils';

type ListElementModalProps = {
  t?: TFunction<undefined>;
  id: string;
  equipmentClassId?: number;
  onClickValidButton?: () => void;
  isElement: boolean;
  newRectangle: Rectangle;
  newPolygon: Polygon;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  onCancel: () => void;
  mode?: string;
};

const ListAddElementModal = ({
  t,
  id,
  equipmentClassId,
  onClickValidButton,
  isElement,
  newRectangle,
  newPolygon,
  canvasRef,
  onCancel,
  mode,
}: ListElementModalProps) => {
  const {
    setReportEngineData,
    setListDamage,
    setListElement,
    setDataAnnotation,
    setDataAnnotationDamage,
    dataAnnotation,
    dataAnnotationDamage,
    reportEngineData,
  } = useContext(EngineCorrectionContext);
  const [option, setOption] = useState<string>('0');
  const [dmgLevel, setDmgLevel] = useState<number>(1);
  const validateButtonRef = useRef<HTMLButtonElement | null>(null);

  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { uniqueDamagesSelectOptions, allDamagesSelectOptions } =
    useGetModerationDamages();

  useEffect(() => {
    if (option || dmgLevel) {
      focusButton(validateButtonRef);
    }
    // Reset all values when a new shape is drawn
    setOption(isElement ? equipmentClassId?.toString() || '' : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newRectangle, newPolygon]);

  useEffect(() => {
    setDmgLevel(1);
  }, [isElement, equipmentClassId]);

  const getOptions = useMemo(
    () =>
      isElement
        ? equipmentClassId
          ? equipmentsSelectOptions.filter(
              (option) => option?.value === equipmentClassId?.toString(),
            )
          : equipmentsSelectOptions
        : uniqueDamagesSelectOptions,
    [
      isElement,
      equipmentClassId,
      equipmentsSelectOptions,
      uniqueDamagesSelectOptions,
    ],
  );

  const onValidChoice = useCallback(() => {
    const idReport = reportEngineData.idReport;
    const dmgValueAfter = getDamageValue(
      option,
      dmgLevel,
      allDamagesSelectOptions,
    );
    const newElement: ElementType = {
      color: getColorByValue(
        isElement ? option : dmgValueAfter,
        isElement,
        equipmentsSelectOptions,
        allDamagesSelectOptions,
      ),
      elNumber: isElement ? option : dmgValueAfter,
    };
    const newAnnotation = convertRectangePointToYolo(
      isElement ? option : dmgValueAfter,
      newRectangle,
      newPolygon,
      canvasRef,
      mode === MODE_DRAW.RECTANGLE,
    );
    if (isElement) {
      let newLinkMapped = {};
      const dmgInside = findDmgInsideEl(dataAnnotationDamage, newAnnotation);
      if (dmgInside.length > 0) {
        newLinkMapped = {
          linkElDmgCorrectionType: CORRECTION_TYPE.AJOUT,
          linkElDmgAfterCorrectionEl: newElement.elNumber,
          linkElDmgBeforeCorrectionDmg: dmgInside[0].elNumber.toString(),
          linkElDmgAfterCorrectionDmg: dmgInside[0].elNumber.toString(),
        };
      }

      setListElement((prevValue) => [...prevValue, newElement]);
      setDataAnnotation((prev) => [...prev, newAnnotation]);
      setReportEngineData((prev) => ({
        ...prev,
        create: [
          ...prev.create,
          {
            ...initReport(idReport),
            isCorrectElements: false,
            elmtCorrectionTypes: CORRECTION_TYPE.AJOUT,
            elmtAfterCorrection: newElement.elNumber,
            ...newLinkMapped,
          },
        ],
      }));
    } else if (!isElement) {
      const parentEl = findElementParentDmg(newAnnotation, dataAnnotation);
      let linkObj = {};
      if (parentEl) {
        linkObj = {
          linkElDmgCorrectionType: CORRECTION_TYPE.AJOUT,
          linkElDmgAfterCorrectionDmg: newElement.elNumber,
          linkElDmgAfterCorrectionEl: parentEl.elNumber.toString(),
          linkElDmgBeforeCorrectionEl: parentEl.elNumber.toString(),
        };
      }
      setListDamage((prevValue) => [...prevValue, newElement]);
      setDataAnnotationDamage((prev) => [...prev, newAnnotation]);
      setReportEngineData((prev) => ({
        ...prev,
        create: [
          ...prev.create,
          {
            ...initReport(idReport),
            isCorrectDamages: false,
            dmgCorrectionTypes: CORRECTION_TYPE.AJOUT,
            dmgAfterCorrection: newElement.elNumber,
            dmgLevelAfterCorrection: String(dmgLevel),
            ...linkObj,
          },
        ],
      }));
    }

    if (onClickValidButton) {
      onClickValidButton();
    }
  }, [isElement, option, newRectangle, dmgLevel, newPolygon, mode]);

  return (
    <Modal
      id={id}
      title={isElement ? t('pleaseChooseEquipment') : t('pleaseChooseDamage')}
      size="small"
      centerY
      secondBtnLabel={t('common:validate')}
      showSecondBtn
      secondButtonRef={validateButtonRef}
      disabledSubmitButton={!option || !dmgLevel}
      onClickSecondBtn={onValidChoice}
      onClickCancelBtn={onCancel}
      onClose={onCancel}
    >
      <div className="grid gap-4">
        <div>
          <div className="text-2sm text-slate-500 font-medium mb-0.5">
            <span>
              {isElement ? t('common:equipment') : t('common:damage')}
            </span>
            <span className="ml-1 mt-0.5 text-red-FF2929 text-md">*</span>
          </div>
          <Select
            selectClassName="w-[319px] text-2sm"
            options={getOptions}
            placeholder={
              isElement
                ? equipmentClassId
                  ? undefined
                  : t('common:chooseAnEquipment')
                : t('common:chooseADamage')
            }
            value={option}
            onChange={(e) => {
              setOption(e.target.value);
              focusButton(validateButtonRef);
            }}
          />
        </div>
        {!isElement && (
          <div>
            <div className="text-2sm text-slate-500 font-medium mb-0.5">
              {t('common:severity')}
            </div>
            <Select
              selectClassName="text-2sm"
              options={damageLevelOptions}
              value={dmgLevel}
              onChange={(e) => {
                setDmgLevel(Number(e.target.value));
                focusButton(validateButtonRef);
              }}
              defaultValue="1"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ListAddElementModal;
